import type { AreaComponent, CylinderPlacementIssue, SizeMm, Surface } from '@repo/api-contracts';
import { initialCylinderPlacementIssue } from '@/entities/edit-area';
import { formatMmChange } from '@/shared/lib';

/**
 * 원통 표면 가운데에 둘 수 없는 사유별 안내. 인쇄 영역 값과 한계 값(둘레 2W·프레임 높이)은 거부 판정과 같은 허용오차
 * (formatMmChange 가 쓰는 계약 isSameMm)로 견줘 보인다 — 허용오차를 넘게 큰데 소수 둘째 자리 표시가 같으면(229.3363 vs
 * 둘레 229.33626…) 차이가 보일 때까지 자리를 늘려, 같은 숫자로 보이는데 거부된다고 안내하지 않는다(drift 안내와 같은 규칙).
 * 화면 표시 전용이다.
 */
const CYLINDER_NO_ROOM: Readonly<
  Record<CylinderPlacementIssue, (component: AreaComponent, frame: SizeMm) => string>
> = {
  WIDER_THAN_CIRCUMFERENCE: (component, frame) => {
    const [width, circumference] = formatMmChange(component.widthMm, 2 * frame.widthMm);
    return `‘${component.name}’의 폭 ${width}mm가 원통 둘레 ${circumference}mm보다 커서 이 표면에 배치할 수 없습니다. 인쇄 영역 크기를 확인해 주세요.`;
  },
  OUTSIDE_FRAME_HEIGHT: (component, frame) => {
    const [height, frameHeight] = formatMmChange(component.heightMm, frame.heightMm);
    return `‘${component.name}’의 높이 ${height}mm가 원통 프레임 높이 ${frameHeight}mm보다 커서 이 표면에 배치할 수 없습니다. 인쇄 영역 크기를 확인해 주세요.`;
  },
  NO_HORIZONTAL_OVERLAP: (component) =>
    `‘${component.name}’이(가) 원통 프레임과 가로로 겹치지 않아 이 표면에 배치할 수 없습니다. 인쇄 영역 크기를 확인해 주세요.`,
};

/**
 * '이 면에 배치'가 자리를 찾지 못한 까닭. 원통 표면은 가운데 위치의 계약 검사 사유(폭 > 둘레 2W, 높이 > 프레임 높이)를
 * 값과 함께 보이고(ADR-0007 D9), 그 밖의 표면은 윤곽 안에서 자리를 찾지 못했다고 안내한다.
 */
export const describeNoRoom = (component: AreaComponent, surface: Surface): string => {
  const issue =
    surface.type === 'cylinder' ? initialCylinderPlacementIssue(component, surface) : null;
  return issue === null
    ? `‘${component.name}’이(가) 이 표면의 제품 윤곽 안에 들어갈 자리가 없습니다. 인쇄 영역 크기나 윤곽을 확인해 주세요.`
    : CYLINDER_NO_ROOM[issue](component, surface.realSize);
};
